import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Box, 
  Typography 
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';

interface FileUploadMAIQProps {
  selectedFile: File | null;
  onFileSelect: (file: File) => void;
  accept?: string;
  id?: string;
  disabled?: boolean;
}

const FileUploadMAIQ: React.FC<FileUploadMAIQProps> = ({ 
  selectedFile, 
  onFileSelect, 
  accept = "*/*",
  id = "file-upload",
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear file input when selectedFile becomes null
  useEffect(() => {
    if (!selectedFile && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFile]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (event.target.files && event.target.files.length > 0) {
      onFileSelect(event.target.files[0]);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, [disabled]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  }, [disabled]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Check if file type is accepted
      if (accept !== "*/*") {
        const acceptedTypes = accept.split(',');
        const fileType = file.type;
        const fileExtension = `.${file.name.split('.').pop()}`;
        
        const isAccepted = acceptedTypes.some(type => 
          type === fileType || 
          type === fileExtension || 
          (type.includes('/*') && fileType.startsWith(type.replace('/*', '/')))
        );
        
        if (!isAccepted) {
          return;
        }
      }
      onFileSelect(file);
    }
  }, [accept, onFileSelect, disabled]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    if (!selectedFile) return <CloudUploadIcon sx={{ fontSize: 60, color: '#ff6600', mb: 2 }} />;
    
    if (selectedFile.name.endsWith('.pdf')) {
      return <PictureAsPdfIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />;
    } else if (selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.xlsx') || selectedFile.name.endsWith('.xls')) {
      return <TableChartIcon sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />;
    } else {
      return <InsertDriveFileIcon sx={{ fontSize: 60, color: '#2196f3', mb: 2 }} />;
    }
  };

  return (
    <Box 
      component="div"
      sx={{ 
        position: 'relative',
        width: '100%',
      }}
    >
      <label
        htmlFor={id}
        style={{
          display: 'block',
          width: '100%'
        }}
      >
        <Box
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{ 
            border: '2px dashed',
            borderColor: disabled 
              ? '#e0e0e0' 
              : (isDragging ? '#0f284c' : (selectedFile ? 'rgba(15, 40, 76, 0.3)' : '#ccc')), 
            borderRadius: '8px', 
            p: 4, 
            textAlign: 'center',
            bgcolor: disabled 
              ? '#f5f5f5'
              : (isDragging 
                ? 'rgba(15, 40, 76, 0.08)'
                : (selectedFile ? 'rgba(15, 40, 76, 0.04)' : 'transparent')),
            transition: 'all 0.3s ease',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '150px',
            width: '100%',
            boxSizing: 'border-box',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              bgcolor: disabled 
                ? '#f5f5f5'
                : 'rgba(15, 40, 76, 0.04)',
              borderColor: disabled 
                ? '#e0e0e0'
                : '#0f284c'
            }
          }}
        >
          <input
            ref={fileInputRef}
            id={id}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            disabled={disabled}
            style={{ display: 'none' }}
          />
          
          {!selectedFile ? (
            <>
              {getFileIcon()}
              <Typography variant="body1" fontWeight={500} sx={{ mb: 1, color: disabled ? 'text.disabled' : 'inherit' }}>
                {disabled ? 'Please select an entity first' : 'Drag and drop file here or click to browse'}
              </Typography>
              <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.secondary'}>
                {disabled ? 'Upload is disabled until entity is selected' : 'Supported formats: CSV, XLSX, PDF'}
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {getFileIcon()}
              <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatFileSize(selectedFile.size)} • Click to change file
              </Typography>
            </Box>
          )}
        </Box>
      </label>
    </Box>
  );
};

export default FileUploadMAIQ; 